import { LitElement, css, html, type PropertyValues } from "lit"
import { customElement, property, query } from "lit/decorators.js"
import { FluidSimulation } from "./core/simulation.js"
import type { ColorMode } from "./types/fluid-types.js"

/**
 * A fluid simulation web component with WebGL-based physics.
 *
 * @fires fluid-ready - Fired when the simulation is initialized
 * @fires fluid-splat - Fired when a splat is created
 * @fires fluid-interaction-start - Fired when user starts interacting
 * @fires fluid-interaction-end - Fired when user stops interacting
 */
@customElement("fluid-pointer")
export class FluidPointer extends LitElement {
	// Sizing properties
	@property({ type: String }) width = "100%"
	@property({ type: String }) height = "400px"
	@property({ type: String }) aspectRatio = ""
	@property({ type: String }) minWidth = "200px"
	@property({ type: String }) minHeight = "150px"
	@property({ type: String }) maxWidth = "none"
	@property({ type: String }) maxHeight = "none"
	@property({ type: Boolean }) responsive = false
	@property({ type: Boolean }) maintainAspectRatio = false

	// Simulation quality
	@property({ type: Number, attribute: "sim-resolution" }) simResolution = 128
	@property({ type: Number, attribute: "dye-resolution" }) dyeResolution = 1024

	// Physics parameters
	@property({ type: Number, attribute: "density-dissipation" })
	densityDissipation = 3.5
	@property({ type: Number, attribute: "velocity-dissipation" })
	velocityDissipation = 5
	@property({ type: Number }) pressure = 0.8
	@property({ type: Number, attribute: "pressure-iterations" })
	pressureIterations = 20
	@property({ type: Number }) curl = 3

	// Interaction
	@property({ type: Number }) splatRadius = 0.25
	@property({ type: Number, attribute: "splat-force" }) splatForce = 6000
	@property({ type: Boolean }) mouseInteraction = true
	@property({ type: Boolean }) touchInteraction = true
	@property({ type: String, attribute: "interaction-mode" }) interactionMode =
		"movement" // 'movement' | 'click' | 'both'
	@property({ type: Number, attribute: "velocity-threshold" })
	velocityThreshold = 0.001

	// Visual effects
	@property({ type: Boolean }) shading = true
	@property({ type: Boolean }) colorful = true
	@property({ type: Number, attribute: "color-update-speed" })
	colorUpdateSpeed = 2
	@property({ type: Number, attribute: "color-transition-speed" })
	colorTransitionSpeed = 0.2
	@property({ type: String, attribute: "color-mode" }) colorMode = "rainbow" // 'rainbow' | 'monochrome' | 'custom'
	@property({ type: String }) color = "" // CSS color string (hex or named color)

	// Control
	@property({ type: Boolean }) paused = false
	@property({ type: Boolean }) autoStart = true

	@query("canvas") private canvas!: HTMLCanvasElement

	private resizeObserver?: ResizeObserver
	private fluidSimulation?: FluidSimulation
	private animationId?: number

	static styles = css`
		:host {
			display: block;
			width: var(--fluid-width);
			height: var(--fluid-height);
			min-width: var(--fluid-min-width);
			min-height: var(--fluid-min-height);
			max-width: var(--fluid-max-width);
			max-height: var(--fluid-max-height);
			aspect-ratio: var(--fluid-aspect-ratio, auto);
			contain: layout style paint;
			box-sizing: border-box;
			position: relative;
			overflow: hidden;
			cursor: var(--fluid-cursor, crosshair);
		}

		canvas {
			width: 100%;
			height: 100%;
			display: block;
			object-fit: cover;
		}

		:host([responsive]) {
			width: 100%;
			height: auto;
		}

		:host([aspect-ratio="16:9"]) {
			aspect-ratio: 16 / 9;
		}

		:host([aspect-ratio="1:1"]) {
			aspect-ratio: 1 / 1;
		}

		:host([aspect-ratio="4:3"]) {
			aspect-ratio: 4 / 3;
		}

		:host([paused]) {
			cursor: var(--fluid-cursor-paused, default);
		}
	`

	protected firstUpdated(_changedProperties: PropertyValues) {
		super.firstUpdated(_changedProperties)

		this.updateCSSVariables()
		this.setupResizeObserver()
		if (this.autoStart) {
			this.initializeFluidSimulation()
		}
	}

	protected updated(changedProperties: PropertyValues) {
		const sizeProps = [
			"width",
			"height",
			"aspectRatio",
			"minWidth",
			"minHeight",
			"maxWidth",
			"maxHeight",
		]
		if (this.hasPropertyChanged(changedProperties, sizeProps)) {
			this.updateCSSVariables()
		}
		super.updated(changedProperties)

		// Handle simulation property changes
		if (
			this.fluidSimulation &&
			this.hasPropertyChanged(changedProperties, [
				"densityDissipation",
				"velocityDissipation",
				"pressure",
				"pressureIterations",
				"curl",
				"splatRadius",
				"splatForce",
				"colorUpdateSpeed",
				"colorTransitionSpeed",
				"colorful",
				"colorMode",
				"color",
				"shading",
			])
		) {
			this.updateSimulationConfig()
		}

		if (changedProperties.has("paused")) {
			if (this.paused) {
				this.pauseSimulation()
			} else {
				this.resumeSimulation()
			}
		}
	}

	private hasPropertyChanged(
		changedProperties: PropertyValues,
		properties: string[]
	): boolean {
		return properties.some((prop) => changedProperties.has(prop))
	}

	private updateCSSVariables() {
		const hostElement = this as unknown as HTMLElement
		hostElement.style.setProperty("--fluid-width", this.width)
		hostElement.style.setProperty("--fluid-height", this.height)
		hostElement.style.setProperty("--fluid-min-width", this.minWidth)
		hostElement.style.setProperty("--fluid-min-height", this.minHeight)
		hostElement.style.setProperty("--fluid-max-width", this.maxWidth)
		hostElement.style.setProperty("--fluid-max-height", this.maxHeight)

		if (this.aspectRatio) {
			hostElement.style.setProperty(
				"--fluid-aspect-ratio",
				this.aspectRatio.replace(":", " / ")
			)
		}
	}

	private setupResizeObserver() {
		this.resizeObserver = new ResizeObserver((entries) => {
			for (const entry of entries) {
				this.handleResize(entry.contentRect)
			}
		})
		this.resizeObserver.observe(this as unknown as Element)
	}

	private handleResize(rect: DOMRectReadOnly) {
		if (this.canvas && this.fluidSimulation) {
			const dpr = window.devicePixelRatio || 1
			this.canvas.width = rect.width * dpr
			this.canvas.height = rect.height * dpr

			// Notify fluid simulation of size change
			try {
				this.fluidSimulation.handleResize?.(rect.width, rect.height)
			} catch (error) {
				console.error("Error during resize:", error)
				// Try to reinitialize if resize fails
				this.initializeFluidSimulation()
			}
		}
	}

	private async initializeFluidSimulation() {
		try {
			this.fluidSimulation = new FluidSimulation(
				this.canvas,
				this.getSimulationConfig()
			)
			await this.fluidSimulation.initialize()

			if (this.autoStart) {
				this.startAnimationLoop()
			}

			// Emit ready event
			const event = new CustomEvent("fluid-ready", {
				detail: { width: this.canvas.width, height: this.canvas.height },
			})
			;(this as unknown as EventTarget).dispatchEvent(event)
		} catch (error) {
			console.error("Failed to initialize fluid simulation:", error)
		}
	}

	private getSimulationConfig() {
		console.log("Getting simulation config")
		console.log("Density dissipation:", this.densityDissipation)
		console.log(
			"html density-dissipation",
			this.getAttribute("density-dissipation")
		)
		return {
			SIM_RESOLUTION: this.simResolution,
			DYE_RESOLUTION: this.dyeResolution,
			DENSITY_DISSIPATION: this.densityDissipation,
			VELOCITY_DISSIPATION: this.velocityDissipation,
			PRESSURE: this.pressure,
			PRESSURE_ITERATIONS: this.pressureIterations,
			CURL: this.curl,
			SPLAT_RADIUS: this.splatRadius,
			SPLAT_FORCE: this.splatForce,
			SHADING: this.shading,
			COLORFUL: this.colorful,
			COLOR_UPDATE_SPEED: this.colorUpdateSpeed,
			COLOR_TRANSITION_SPEED: this.colorTransitionSpeed,
			COLOR_MODE: this.colorMode as ColorMode,
			COLOR: this.color,
			PAUSED: this.paused,
		}
	}

	private updateSimulationConfig() {
		if (this.fluidSimulation) {
			this.fluidSimulation.updateConfig(this.getSimulationConfig())
		}
	}

	private pauseSimulation() {
		if (this.animationId) {
			cancelAnimationFrame(this.animationId)
			this.animationId = undefined
		}
	}

	private resumeSimulation() {
		if (!this.animationId && this.fluidSimulation) {
			this.startAnimationLoop()
		}
	}

	private startAnimationLoop() {
		if (!this.fluidSimulation || this.paused) return

		const animate = () => {
			if (this.paused || !this.fluidSimulation) return
			try {
				this.fluidSimulation.step()
				this.fluidSimulation.render()
			} catch (error) {
				console.error("Error in animation loop:", error)
				// Stop the animation loop on error
				this.pauseSimulation()
				return
			}

			this.animationId = requestAnimationFrame(animate)
		}

		this.animationId = requestAnimationFrame(animate)
	}

	// Public API methods
	public play() {
		this.paused = false
	}

	public pause() {
		this.paused = true
	}

	public reset() {
		// This will reinitialize the simulation
		this.initializeFluidSimulation()
	}

	public addSplat(
		x: number,
		y: number,
		velocityX: number,
		velocityY: number,
		color?: [number, number, number]
	) {
		if (this.fluidSimulation) {
			this.fluidSimulation.splat(x, y, velocityX, velocityY, color)

			// Emit splat event
			const splatEvent = new CustomEvent("fluid-splat", {
				detail: {
					x,
					y,
					velocityX,
					velocityY,
					color: color || [1, 1, 1],
					force: Math.sqrt(velocityX * velocityX + velocityY * velocityY),
				},
			})
			;(this as unknown as EventTarget).dispatchEvent(splatEvent)
		}
	}

	public addRandomSplats(count: number) {
		for (let i = 0; i < count; i++) {
			this.addSplat(
				Math.random(),
				Math.random(),
				(Math.random() - 0.5) * this.splatForce * 0.1,
				(Math.random() - 0.5) * this.splatForce * 0.1
			)
		}
	}

	connectedCallback(): void {
		super.connectedCallback()

		console.log(this.getAttribute("density-dissipation"))
		console.log(this.densityDissipation)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.resizeObserver?.disconnect()
		this.pauseSimulation()
	}

	render() {
		return html`<canvas></canvas>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"fluid-pointer": FluidPointer
	}
}
